import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { DEFAULT_INTAKE_LIST_STATUS } from "@/constants/intakeFilters";
import { formatDateForUrl } from "@/utils/dateUtils";

/**
 * Pinia store for intake list page filter state.
 *
 * This store maintains filter state that persists during the session,
 * allowing navigation to detail pages and back while preserving filters.
 * Applies to intake documents and orders displayed on the intake list page.
 *
 * Two views sync to this store:
 * - Table view (IntakeTablePage) — all fields
 * - Kanban view (PatientsListPage via useIntakeListFilters) — status, patient, date, sortBy
 * `status` and `sortBy` are shared: both views persist them under the same
 * `status`/`sort` URL params.
 *
 * The store works alongside URL query parameters:
 * - URL params are the source of truth on page load
 * - Store syncs with URL params and maintains state during navigation
 * - Components use getNavigationQuery() to get params for router.push()
 */
export const useIntakeListFiltersStore = defineStore("intakeListFilters", () => {
  // Shared filter state (both views)
  const status = ref(DEFAULT_INTAKE_LIST_STATUS);
  const sortBy = ref(null); // null means use default from client config

  // Kanban view filter state
  const patient = ref("");
  const date = ref(null);

  // Table view filter state
  const search = ref("");
  const dateFrom = ref(null);
  const dateTo = ref(null);
  const dueFrom = ref(null);
  const dueTo = ref(null);
  const documentType = ref("");
  const modifiedBy = ref("");
  const sentBy = ref("");
  const tags = ref("");
  const tagsMatch = ref("all");
  const assignedUser = ref("");
  const assignedGroup = ref("");
  const sortKey = ref("");
  const sortDir = ref("desc");
  const page = ref(1);
  const showActionNeeded = ref(true);

  // Computed: check if any non-default kanban filters are active.
  // Deliberately excludes table-only fields — this drives the kanban view's
  // restore-from-store branch, which only knows how to apply these fields.
  const hasActiveFilters = computed(() => {
    return (
      status.value !== DEFAULT_INTAKE_LIST_STATUS ||
      (patient.value && patient.value.trim()) ||
      date.value ||
      sortBy.value
    );
  });

  // Computed: check if any non-default table filters are active (drives the
  // table view's restore-from-store branch).
  const hasTableFilters = computed(() => {
    return Boolean(
      status.value !== DEFAULT_INTAKE_LIST_STATUS ||
        (search.value && search.value.trim()) ||
        dateFrom.value ||
        dateTo.value ||
        dueFrom.value ||
        dueTo.value ||
        sortBy.value ||
        documentType.value ||
        modifiedBy.value ||
        sentBy.value ||
        tags.value ||
        assignedUser.value ||
        assignedGroup.value ||
        sortKey.value ||
        sortDir.value !== "desc" ||
        page.value > 1 ||
        !showActionNeeded.value
    );
  });

  /**
   * Update all filters at once (typically called when URL params change)
   */
  function setFilters({
    status: newStatus,
    patient: newPatient,
    date: newDate,
    sortBy: newSortBy,
    search: newSearch,
    dateFrom: newDateFrom,
    dateTo: newDateTo,
    dueFrom: newDueFrom,
    dueTo: newDueTo,
    documentType: newDocumentType,
    modifiedBy: newModifiedBy,
    sentBy: newSentBy,
    tags: newTags,
    tagsMatch: newTagsMatch,
    assignedUser: newAssignedUser,
    assignedGroup: newAssignedGroup,
    sortKey: newSortKey,
    sortDir: newSortDir,
    page: newPage,
    showActionNeeded: newShowActionNeeded,
  }) {
    if (newStatus !== undefined) {
      status.value = newStatus;
    }
    if (newPatient !== undefined) {
      patient.value = newPatient;
    }
    if (newDate !== undefined) {
      date.value = newDate;
    }
    if (newSortBy !== undefined) {
      sortBy.value = newSortBy;
    }
    if (newSearch !== undefined) {
      search.value = newSearch;
    }
    if (newDateFrom !== undefined) {
      dateFrom.value = newDateFrom;
    }
    if (newDateTo !== undefined) {
      dateTo.value = newDateTo;
    }
    if (newDueFrom !== undefined) {
      dueFrom.value = newDueFrom;
    }
    if (newDueTo !== undefined) {
      dueTo.value = newDueTo;
    }
    if (newDocumentType !== undefined) {
      documentType.value = newDocumentType;
    }
    if (newModifiedBy !== undefined) {
      modifiedBy.value = newModifiedBy;
    }
    if (newSentBy !== undefined) {
      sentBy.value = newSentBy;
    }
    if (newTags !== undefined) {
      tags.value = newTags;
    }
    if (newTagsMatch !== undefined) {
      tagsMatch.value = newTagsMatch;
    }
    if (newAssignedUser !== undefined) {
      assignedUser.value = newAssignedUser;
    }
    if (newAssignedGroup !== undefined) {
      assignedGroup.value = newAssignedGroup;
    }
    if (newSortKey !== undefined) {
      sortKey.value = newSortKey;
    }
    if (newSortDir !== undefined) {
      sortDir.value = newSortDir;
    }
    if (newPage !== undefined) {
      page.value = newPage;
    }
    if (newShowActionNeeded !== undefined) {
      showActionNeeded.value = newShowActionNeeded;
    }
  }

  /**
   * Get query params object for navigation to intake list page.
   * Only includes non-default values to keep URLs clean.
   * Param names match IntakeTablePage.updateUrl() / useIntakeListFilters.
   */
  function getNavigationQuery() {
    const query = {};

    if (status.value && status.value !== DEFAULT_INTAKE_LIST_STATUS) {
      query.status = status.value;
    }

    if (patient.value && patient.value.trim()) {
      query.patient = patient.value.trim();
    }

    const formattedDate = formatDateForUrl(date.value);
    if (formattedDate) {
      query.date = formattedDate;
    }

    if (sortBy.value) {
      query.sort = sortBy.value;
    }

    if (search.value && search.value.trim()) {
      query.search = search.value.trim();
    }

    const formattedDateFrom = formatDateForUrl(dateFrom.value);
    if (formattedDateFrom) {
      query.dateFrom = formattedDateFrom;
    }

    const formattedDateTo = formatDateForUrl(dateTo.value);
    if (formattedDateTo) {
      query.dateTo = formattedDateTo;
    }

    if (documentType.value) {
      query.documentType = documentType.value;
    }

    if (modifiedBy.value) {
      query.modifiedBy = modifiedBy.value;
    }

    if (sentBy.value) {
      query.sentBy = sentBy.value;
    }

    if (tags.value) {
      query.tags = tags.value;
      if (tagsMatch.value === "any") {
        query.tagsMatch = "any";
      }
      const formattedDueFrom = formatDateForUrl(dueFrom.value);
      if (formattedDueFrom) {
        query.dueFrom = formattedDueFrom;
      }
      const formattedDueTo = formatDateForUrl(dueTo.value);
      if (formattedDueTo) {
        query.dueTo = formattedDueTo;
      }
    }

    if (assignedUser.value) {
      query.assignedUser = assignedUser.value;
    }

    if (assignedGroup.value) {
      query.assignedGroup = assignedGroup.value;
    }

    if (sortKey.value) {
      query.sortKey = sortKey.value;
    }

    if (sortDir.value && sortDir.value !== "desc") {
      query.sortDir = sortDir.value;
    }

    if (page.value > 1) {
      query.page = String(page.value);
    }

    return query;
  }

  /**
   * Clear all filters to defaults
   */
  function clearFilters() {
    status.value = DEFAULT_INTAKE_LIST_STATUS;
    patient.value = "";
    date.value = null;
    sortBy.value = null;
    search.value = "";
    dateFrom.value = null;
    dateTo.value = null;
    dueFrom.value = null;
    dueTo.value = null;
    documentType.value = "";
    modifiedBy.value = "";
    sentBy.value = "";
    tags.value = "";
    tagsMatch.value = "all";
    assignedUser.value = "";
    assignedGroup.value = "";
    sortKey.value = "";
    sortDir.value = "desc";
    page.value = 1;
    showActionNeeded.value = true;
  }

  return {
    // State
    status,
    patient,
    date,
    sortBy,
    search,
    dateFrom,
    dateTo,
    dueFrom,
    dueTo,
    documentType,
    modifiedBy,
    sentBy,
    tags,
    tagsMatch,
    assignedUser,
    assignedGroup,
    sortKey,
    sortDir,
    page,
    showActionNeeded,

    // Computed
    hasActiveFilters,
    hasTableFilters,

    // Actions
    setFilters,
    getNavigationQuery,
    clearFilters,
  };
});
